<template>
  <div data-testid="appointment-details-yard-location-box">
    <h4 class="d-flex align-center mb-3">
      {{ t('yard.headers.yardLocation') }}
      <v-spacer />
      <v-chip size="x-small" variant="tonal" v-if="assetVisitId">
        <strong>{{ assetVistTypeFormatted }}</strong>
      </v-chip>
    </h4>
    <v-progress-circular v-if="isLoading" indeterminate color="secondary" size="15" />

    <div v-else-if="assetVisitId">
      <yard-view-appointment-details-yard-location
        v-if="spotAreaName && spotCode && spotId"
        :spot-area-name="spotAreaName"
        :spot-id="spotId"
        :spot-code="spotCode"
        :warehouse-id="warehouseId" />
      <p v-else class="typography-body-sm">
        <span v-if="assetVisitDeparted">{{ t('yard.messages.carrierDepartedFromTheYard') }}.</span>
        <span v-else>{{ t('yard.messages.carrierWaitingToBeAssignedToASpot') }}.</span>
      </p>
    </div>

    <p v-else class="text--color-text-tertiary typography-body-sm">
      {{ t('yard.messages.carrierHasntCheckedIn') }}.
    </p>

    <yard-view-appointment-details-asset-details
      :asset-containers="assetContainers"
      :warehouse-id="warehouseId"
      :appointment-id="appointmentId"
      :asset-visit-type="assetVisitType" />
  </div>
</template>

<script>
import { defineComponent, onMounted, ref, computed, onBeforeMount, onBeforeUnmount } from 'vue';
import { first } from 'lodash';

import { isAssetVisitDeparted } from '@nova/core';
import YardViewAppointmentDetailsAssetDetails from './YardViewAppointmentDetailsAssetDetails.vue';
import YardViewAppointmentDetailsYardLocation from './YardViewAppointmentDetailsYardLocation.vue';
import assetVisitService from '@satellite/services/asset-visit-service';
import useYard from '@/modules/yard/composables/useYard';
import { useEventHub, useSockets } from '@/composables';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAppointmentDetailsBox',

  components: {
    YardViewAppointmentDetailsAssetDetails,
    YardViewAppointmentDetailsYardLocation
  },

  props: {
    appointmentId: {
      type: String,
      required: true
    }
  },

  setup(props) {
    const yardStore = useYard();
    const sockets = useSockets();
    const eventHub = useEventHub();
    const { t } = useI18n();

    const isLoading = ref(false);
    const spotAreaName = ref('');
    const spotCode = ref('');
    const assetVisitType = ref('');
    const assetVisitDeparted = ref(false);
    const spotId = ref('');
    const assetVisitId = ref('');
    const warehouseId = ref('');
    const assetContainers = ref([]);

    const assetVistTypeFormatted = computed(() =>
      yardStore.getAssetVisitTypeDisplayName(assetVisitType.value)
    );

    const fetchData = async () => {
      isLoading.value = true;
      try {
        const response = await assetVisitService.getAssetVisitByAppointmentId(props.appointmentId, {
          params: {
            join: [
              'assetVisitEvents',
              'spotAssignments',
              'assetContainers',
              'assetContainers.assetContainerEvents',
              'spotAssignments.spot',
              'spotAssignments.spot.spotArea'
            ]
          }
        });

        if (response?.id) {
          assetVisitId.value = response.id;
          assetVisitType.value = response.visitType;
          warehouseId.value = response.warehouseId;
          assetContainers.value = response.assetContainers || [];
          assetVisitDeparted.value = isAssetVisitDeparted(response);

          const spotAssignment = first(response.spotAssignments);
          if (spotAssignment) {
            spotAreaName.value = spotAssignment.spot.spotArea.name;
            spotCode.value = spotAssignment.spot.code;
            spotId.value = spotAssignment.spot.id;
          } else {
            spotAreaName.value = '';
            spotCode.value = '';
            spotId.value = '';
          }
        }
      } catch (error) {
        console.error('Error fetching appointment details:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const handleYardSubspaceEvent = async event => {
      if (
        spotId.value === event.spotId ||
        assetVisitId.value === event.assetVisitId ||
        assetVisitId.value === event.id ||
        event.pickupAppointmentId === props.appointmentId ||
        event.dropAppointmentId === props.appointmentId ||
        event.appointmentId === props.appointmentId
      ) {
        return fetchData();
      }

      assetContainers.value.forEach(ac => {
        if (ac.id === event.assetContainerId || ac.id === event.id) {
          return fetchData();
        }
      });
    };

    onMounted(fetchData);

    onBeforeMount(() => {
      Object.keys(sockets.actions).forEach(operation => {
        eventHub.$on(`${operation}-SpotAssignment`, handleYardSubspaceEvent);
        eventHub.$on(`${operation}-AssetContainer`, handleYardSubspaceEvent);
        eventHub.$on(`${operation}-AssetContainerEvent`, handleYardSubspaceEvent);
        eventHub.$on(`${operation}-AssetVisit`, handleYardSubspaceEvent);
        eventHub.$on(`${operation}-AssetVisitEvent`, handleYardSubspaceEvent);
      });
    });

    onBeforeUnmount(() => {
      Object.keys(sockets.actions).forEach(operation => {
        eventHub.$off(`${operation}-SpotAssignment`, handleYardSubspaceEvent);
        eventHub.$off(`${operation}-AssetContainer`, handleYardSubspaceEvent);
        eventHub.$off(`${operation}-AssetContainerEvent`, handleYardSubspaceEvent);
        eventHub.$off(`${operation}-AssetVisit`, handleYardSubspaceEvent);
        eventHub.$off(`${operation}-AssetVisitEvent`, handleYardSubspaceEvent);
      });
    });

    return {
      isLoading,
      spotAreaName,
      assetVisitDeparted,
      spotCode,
      assetVisitType,
      assetVisitId,
      spotId,
      assetContainers,
      assetVistTypeFormatted,
      warehouseId,
      t
    };
  }
});
</script>
